// The hero quest intent: a house gives one of its heroes an order. Each
// answers whether the sim took it.

import type { createRustSimAdapter } from '@aod/sim-adapter';
import { type RefObject, useMemo } from 'react';
import type { HeroQuestKind } from '../../heroes/quests.js';

export interface HeroQuestActions {
  /** Give a hero an order: "slay N foes", or "hunt this enemy hero". */
  readonly dispatchHeroQuest: (
    unitId: string,
    kind: HeroQuestKind,
    amount?: number,
    targetUnitId?: string,
  ) => boolean;
}

/** What a sim without the module (or before it boots) offers: nothing happens. */
export const NO_HERO_QUESTS: HeroQuestActions = {
  dispatchHeroQuest: () => false,
};

export function useHeroQuestIntents(
  adapterRef: RefObject<ReturnType<typeof createRustSimAdapter> | null>,
  localPlayerIndex: number,
): HeroQuestActions {
  return useMemo(() => {
    const send = (intent: Record<string, unknown>): boolean => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      return adapter.intent({ ...intent, playerIndex: localPlayerIndex } as never).ok;
    };
    return {
      dispatchHeroQuest: (unitId, kind, amount, targetUnitId) => {
        // The order's own kind is `questKind`: the intent's `kind` is how the
        // intent is routed, and one object cannot carry both.
        const intent: Record<string, unknown> = {
          kind: 'dispatch_hero_quest',
          unitId,
          questKind: kind,
        };
        if (amount !== undefined) intent.amount = amount;
        if (targetUnitId !== undefined) intent.targetId = targetUnitId;
        return send(intent);
      },
    };
  }, [adapterRef, localPlayerIndex]);
}
